import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { InputProps } from '../../../../@types/props-types'
import { camelCaseToDash } from '../../../../utils/text/text-utils'

import './ToggleSwitch.css'
import { Variant } from '../../../../utils/style-utils'
import { ContentStateProperty } from '../../../../@types/types'

export type ToggleSwitchAlignment = 'left' | 'right' | 'default' | 'single' | 'custom' | 'customWithTitle' | 'maxSpread'

export interface ToggleSwitchProps extends Omit<InputProps, 'propertyName'> {
  value: boolean
  className?: string
  propertyName: ContentStateProperty
  labelStyle?: React.CSSProperties
  label?: string | React.ReactNode
  disabled?: boolean
  alignment?: ToggleSwitchAlignment
  togglePlacement?: Variant
  onChange: (field: ContentStateProperty, value: boolean) => void
  onBlur?: () => void
  onBlurTracking?: (field: ContentStateProperty, value: boolean, isUnchanged: boolean) => void
  onFocus?: () => void
}

const ToggleSwitch: React.FunctionComponent<ToggleSwitchProps> = ({
  id,
  propertyName,
  className,
  labelStyle,
  label,
  value,
  disabled,
  alignment = 'default',
  togglePlacement,
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<{
    isUnchanged: boolean
    lastValue: boolean
    currentChecked: boolean
  }>({
    isUnchanged: true,
    lastValue: value,
    currentChecked: value,
  })

  useEffect(() => setState((prev) => ({ ...prev, currentChecked: value })), [value])

  const doChange = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
    onChange(propertyName, checked)
    setState((prev) => ({ ...prev, currentChecked: checked }))
  }

  const onLostFocus = ({
    relatedTarget,
    currentTarget: { checked: newChecked },
  }: React.FocusEvent<HTMLInputElement>) => {
    if (relatedTarget?.id !== `${id}-toggle-switch`) {
      onBlur && onBlur()
    }
    if (state.lastValue !== state.currentChecked && relatedTarget?.id !== `${id}-toggle-switch`) {
      onBlurTracking && onBlurTracking(propertyName, newChecked, state.isUnchanged)
      setState((prev) => ({ ...prev, lastValue: newChecked, isUnchanged: false }))
    }
  }

  const handleLabelClick = () => {
    if (!disabled) {
      inputRef.current?.focus({ preventScroll: true })
    }
  }

  const labelClassNames = new Set(['label-toggle-switch'])
  if (alignment !== 'default') {
    labelClassNames.add('float-left')
  }
  if (alignment === 'left' || alignment === 'right') {
    labelClassNames.add('padding-left-none')
  }
  if (className) {
    labelClassNames.add(className)
  }
  const divClassName = `toggle-switch-container toggle-switch-align-${
    alignment === 'custom' ? 'single' : camelCaseToDash(alignment)
  } ${togglePlacement === undefined ? '' : `float-${togglePlacement}`}`

  const toggle = (
    <div id={`${id}-toggle-switch`} className={divClassName} tabIndex={-1}>
      <input
        ref={inputRef}
        tabIndex={0}
        type='checkbox'
        className='input-toggle-switch'
        id={id}
        disabled={disabled}
        checked={state.currentChecked}
        onChange={doChange}
        onBlur={onLostFocus}
        {...(onFocus && { onFocus: onFocus })}
      />

      <label
        style={labelStyle}
        htmlFor={id}
        className={[...labelClassNames].join(' ')}
        id={id + '-toggle-label'}
        onClick={handleLabelClick}
      >
        {alignment !== 'custom' && alignment !== 'customWithTitle' && alignment !== 'maxSpread' ? <>{label}</> : null}
      </label>
    </div>
  )

  return (
    <>
      {(alignment === 'custom' || alignment === 'customWithTitle') && (
        <>
          <div className='float-left two-third lap-one-half desk-one-half'>
            <label className='font-body-medium-bold' htmlFor={id} id={`${id}-label`} onClick={handleLabelClick}>
              {label}
            </label>
          </div>
          <div className='padding-left-m inline-block one-third desk-one-half'>{toggle}</div>
        </>
      )}
      {alignment === 'maxSpread' && (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <label
            className='font-body-medium-regular'
            htmlFor={id}
            id={`${id}-label`}
            style={{ flex: '1' }}
            onClick={handleLabelClick}
          >
            {label}
          </label>
          <div style={{ marginLeft: 'auto' }}>{toggle}</div>
        </div>
      )}
      {alignment !== 'custom' && alignment !== 'customWithTitle' && alignment !== 'maxSpread' && toggle}
    </>
  )
}

export default ToggleSwitch
